import React, { useEffect, useRef } from "react";
import { Card, CardBody, Row, Col, Input, Label, FormGroup } from "reactstrap";
import { useSelector } from "react-redux";
import { StepComponentProps } from "../types/proposalForm.types";

const Step4Employment: React.FC<StepComponentProps> = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
}) => { 
  const employmentData = useSelector((state: any) => state.kyc?.employmentData ?? []);
  const lastPrefilledEmploymentIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (!employmentData?.length) {
      lastPrefilledEmploymentIdRef.current = null;
      return;
    }
    const e = employmentData[0];
    const recordId = e?.id != null ? Number(e.id) : -1;
    if (lastPrefilledEmploymentIdRef.current === recordId) return;
    lastPrefilledEmploymentIdRef.current = recordId;
    setFieldValue("occupation", e.occupation ?? "");
    setFieldValue("industry_type", e.industry_type ?? "");
    setFieldValue("employername", e.employername ?? "");
    setFieldValue("income_currency", e.income_currency ?? "");
    setFieldValue("total_anual_income", e.total_anual_income ?? "");
    setFieldValue("employer_address", e.employer_address ?? "");
    setFieldValue("income_source", e.income_source ?? "");
    setFieldValue("risk_assessment", e.isoccupationrisk === 1 ? "Yes" : "No");
    setFieldValue("occupationremarks", e.occupationremarks ?? "");
  }, [employmentData, setFieldValue]);
  const renderInput = (
    name: keyof typeof values,
    label: string,
    type: "text" | "number" | "date" | "email" | "tel" = "text",
    placeholder?: string,
    required: boolean = false
  ) => (
    <FormGroup className="form-group">
      <Label>
        {label}
        {required && <span className="text-danger">*</span>}
      </Label>
      <Input
        type={type}
        name={name as string}
        value={String(values[name] || "")}
        onChange={handleChange}
        onBlur={handleBlur}
        invalid={!!(touched[name] && errors[name])}
        placeholder={placeholder}
        className="form-control"
      />
      {touched[name] && errors[name] && (
        <div className="invalid-feedback">{errors[name]}</div>
      )}
    </FormGroup>
  );

  const renderTextarea = (
    name: keyof typeof values,
    label: string,
    placeholder?: string,
    required: boolean = false
  ) => (
    <FormGroup className="form-group">
      <Label>
        {label}
        {required && <span className="text-danger">*</span>}
      </Label>
      <Input
        type="textarea"
        name={name as string}
        value={String(values[name] || "")}
        onChange={handleChange}
        onBlur={handleBlur}
        invalid={!!(touched[name] && errors[name])}
        placeholder={placeholder}
        className="form-control"
        rows={3}
      />
      {touched[name] && errors[name] && (
        <div className="invalid-feedback">{errors[name]}</div>
      )}
    </FormGroup>
  );

  return (
    <>
      <Card className="form-card shadow-sm border-0">
        <CardBody>
          <h5 className="form-title">Employment & Income Details</h5>
          <p className="form-subtitle">
            Please provide your Occupation and income sources.
          </p>

          <Row>
            <Col md={6}>
              {renderInput("occupation", "Occupation", "text", undefined, true)}
            </Col>
            <Col md={6}>
              {renderInput("industry_type", "Industry Type", "text", undefined, true)}
            </Col>

            <Col md={4}>
              {renderInput("employername", "Employer Name", "text", "Employer name", true)}
            </Col>
            <Col md={4}>
              {renderInput("income_currency", "Income Currency", "text", undefined, true)}
            </Col>
            <Col md={4}>
              {renderInput("total_anual_income", "Total Annual Income", "number", "123", true)}
            </Col>

            <Col md={12}>
              {renderTextarea(
                "employer_address",
                "Address of the Employer",
                "Type here",
                true
              )}
            </Col>

            <Col md={12}>
              {renderInput("income_source", "Source of Income", "text", undefined, true)}
            </Col>
          </Row>
        </CardBody>
      </Card>

      <Card className="form-card shadow-sm border-0" style={{ marginTop: "2rem" }}>
        <CardBody>
          <h5 className="form-title">Risk Assessment</h5>
          
          <FormGroup className="form-group">
            <p className="risk-assessment-question">
              Does your occupation require you to work at heights over 10 meter, work underground, offshore at remote locations, handling or transporting hazardous duties not mentioned here? If yes please specify.
            </p>
            <div className="risk-assessment-options">
              {(["Yes", "No"] as const).map((option) => (
                <div key={option} className="risk-assessment-option">
                  <input
                    type="radio"
                    id={`risk_assessment-${option}`}
                    name="risk_assessment"
                    value={option}
                    checked={values.risk_assessment === option}
                    onChange={(e) => setFieldValue("risk_assessment", e.target.value)}
                    className="risk-assessment-radio"
                  />
                  <label htmlFor={`risk_assessment-${option}`} className="risk-assessment-label">
                    {option}
                  </label>
                </div>
              ))}
            </div>
            {values.risk_assessment === "Yes" && (
              <div className="risk-assessment-textarea-wrapper">
                <Input
                  type="textarea"
                  name="occupationremarks"
                  value={String(values.occupationremarks || "")}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  invalid={!!(touched.occupationremarks && errors.occupationremarks)}
                  placeholder="Type here"
                  className="form-control"
                  rows={3}
                />
                {touched.occupationremarks && errors.occupationremarks && (
                  <div className="invalid-feedback">{errors.occupationremarks}</div>
                )}
              </div>
            )}
            {touched.risk_assessment && errors.risk_assessment && (
              <div className="invalid-feedback">{errors.risk_assessment}</div>
            )}
          </FormGroup>
        </CardBody>
      </Card>
    </>
  );
};

export default Step4Employment;
